import React, { useEffect, useRef, useState } from 'react';

const TARGET_PEAKS = 80;

/**
 * Real waveform bars decoded client-side via the Web Audio API from the
 * item's own media — never a fabricated shape. Peaks are reported back via
 * `onWaveformReady` so UnifiedEditor can cache them onto the item's
 * `audio.waveform` (persisted, so a reload doesn't need to re-decode).
 */
export function EditorAudioWaveform({ item, streamUrl, onWaveformReady }) {
  const cached = item.audio?.waveform?.peaks;
  const [peaks, setPeaks] = useState(cached && cached.length ? cached : null);
  const [failed, setFailed] = useState(false);
  const requestedRef = useRef(false);

  useEffect(() => {
    if (peaks || requestedRef.current || !streamUrl) return;
    requestedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) { setFailed(true); return; }
        const res = await fetch(streamUrl);
        const buf = await res.arrayBuffer();
        const ctx = new AudioCtx();
        const decoded = await ctx.decodeAudioData(buf);
        const channel = decoded.getChannelData(0);
        const blockSize = Math.max(1, Math.floor(channel.length / TARGET_PEAKS));
        const nextPeaks = [];
        for (let i = 0; i < TARGET_PEAKS; i++) {
          const start = i * blockSize;
          let max = 0;
          for (let j = start; j < Math.min(start + blockSize, channel.length); j++) {
            max = Math.max(max, Math.abs(channel[j]));
          }
          nextPeaks.push(Math.min(1, max));
        }
        ctx.close?.();
        if (cancelled) return;
        setPeaks(nextPeaks);
        onWaveformReady?.({
          status: 'generated', peaks: nextPeaks,
          samples_per_second: TARGET_PEAKS / (decoded.duration || item.duration_seconds || 1),
          source_duration_seconds: decoded.duration, generated_at: new Date().toISOString(),
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [streamUrl, peaks, item.duration_seconds, item.id, onWaveformReady]);

  if (!peaks) {
    return <div className="h-4 flex items-center px-1 text-[8px] text-white/30" data-testid={`waveform-pending-${item.id}`}>{failed ? '' : '···'}</div>;
  }

  return (
    <div className="h-4 flex items-end gap-px px-0.5 overflow-hidden pointer-events-none" data-testid={`waveform-${item.id}`}>
      {peaks.map((p, i) => (
        <div key={i} className="flex-1 bg-white/50 rounded-sm" style={{ height: `${Math.max(8, p * 100)}%` }} />
      ))}
    </div>
  );
}
